import { useState, useEffect } from 'react';

// API base URL - update this for production
const API_URL = import.meta.env.PROD 
  ? 'https://organise-after-am-api.icy-breeze-c97d.workers.dev/api' 
  : 'http://localhost:8787/api';

// Enhanced authentication hook with API integration
export function useAuth() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Check if user is already logged in
    const storedAuth = localStorage.getItem('am_auth');
    if (storedAuth) {
      try {
        const authData = JSON.parse(storedAuth);
        // Verify token with API
        verifyToken(authData.token);
      } catch (e) {
        localStorage.removeItem('am_auth');
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async (token) => {
    try {
      const response = await fetch(`${API_URL}/auth/verify`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setIsAuthenticated(true);
        setUser(data.user);
      } else {
        localStorage.removeItem('am_auth');
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      localStorage.removeItem('am_auth');
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username, password })
      });

      const data = await response.json();

      if (data.success) {
        const authData = {
          token: data.token,
          user: data.user
        };
        localStorage.setItem('am_auth', JSON.stringify(authData));
        setIsAuthenticated(true);
        setUser(data.user.username);
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Invalid credentials' };
      }
    } catch (error) {
      console.error('Login failed:', error);
      return { success: false, error: 'Connection failed. Please try again.' };
    }
  };

  const logout = () => {
    localStorage.removeItem('am_auth');
    setIsAuthenticated(false);
    setUser(null);
  };

  const getAuthHeader = () => {
    const storedAuth = localStorage.getItem('am_auth');
    if (storedAuth) {
      const authData = JSON.parse(storedAuth);
      return { 'Authorization': `Bearer ${authData.token}` };
    }
    return {};
  };

  return { 
    isAuthenticated, 
    user, 
    login, 
    logout, 
    loading,
    getAuthHeader,
    apiUrl: API_URL
  };
}

// API helper functions
export const api = {
  async get(endpoint, auth) {
    const response = await fetch(`${API_URL}${endpoint}`, {
      headers: {
        ...auth.getAuthHeader(),
        'Content-Type': 'application/json'
      }
    });
    return response.json();
  },

  async post(endpoint, data, auth) {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: {
        ...auth.getAuthHeader(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    return response.json();
  },

  async put(endpoint, data, auth) {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'PUT',
      headers: {
        ...auth.getAuthHeader(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    return response.json();
  },

  async delete(endpoint, auth) {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'DELETE',
      headers: {
        ...auth.getAuthHeader()
      }
    });
    return response.json();
  }
};
